import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { router } from './routes';
import { gaEnabled, setUserId, setUserProperty, trackPageView } from './lib/analytics';
import { useAuthStore } from './stores/authStore';
import './styles/index.css';

// Initialize Sentry error monitoring (only when DSN is configured and in production)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn && sentryDsn.length > 0) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_ENVIRONMENT ?? 'development',
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    enabled: import.meta.env.PROD,
  });
}

// Wire GA4 page_view on every SPA navigation. The bootstrap in index.html sets
// send_page_view:false so we have exactly one page_view per route change.
if (gaEnabled) {
  const standaloneMql = window.matchMedia('(display-mode: standalone)');
  const reportDisplayMode = (isStandalone: boolean): void => {
    setUserProperty('pwa_display_mode', isStandalone ? 'standalone' : 'browser');
  };
  reportDisplayMode(standaloneMql.matches);
  standaloneMql.addEventListener('change', (e) => reportDisplayMode(e.matches));

  // Initial page_view + user_id (from rehydrated auth store, if any).
  setUserId(useAuthStore.getState().user?.id ?? null);
  let lastPath = window.location.pathname;
  trackPageView(lastPath);

  // Unsubscribes intentionally discarded — these listeners live for the app's lifetime.
  void router.subscribe((state) => {
    const path = state.location.pathname;
    if (path === lastPath) return;
    lastPath = path;
    // Defer one microtask so the new route's effect has set document.title
    // before GA4's auto-collected context reads it.
    queueMicrotask(() => trackPageView(path));
  });

  // Keep user_id in sync with auth state: fires on login, logout, and rehydration.
  let lastUserId = useAuthStore.getState().user?.id ?? null;
  void useAuthStore.subscribe((state) => {
    const nextUserId = state.user?.id ?? null;
    if (nextUserId === lastUserId) return;
    lastUserId = nextUserId;
    setUserId(nextUserId);
  });
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Failed to find the root element');
}

// Log when app is ready to work offline
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Service worker is registered by vite-plugin-pwa
    // This listener just logs the offline-ready state
    navigator.serviceWorker.ready.then((registration) => {
      console.log('Service Worker ready:', registration.scope);

      // Register for background sync if supported
      if ('sync' in registration) {
        // Background sync is available
        console.log('Background Sync API available');
      }
    });
  });
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>
);
